import React, { useState, useEffect, useRef } from "react";
import { ref, onValue, update } from 'firebase/database';
import { database, firestore } from '../../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import { resolveFooterForSend } from './resolveFooterForSend';
import { withResolvedImageAltText, buildImagePromptVariables } from './dynamicImageUtils';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../../utils/cloudFunctions';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { selectInstructionByPacing, greetingToneFromVariant, getEffectivePacing, extractItemId, extractItemText, hasInstructionBlocks, stripInstructionBlocks } from './emailTrackingUtils';
import { getAllContactEmails, getRecipientInfoForContact } from '../../shared/enums';
import { emailGenerationCost } from '../../config/creditPlans';
// 🔒 AUDIT: Import für Team-Member-Tracking
import { useActiveWorld } from '../../hooks/useActiveWorld';
import useCustomFields from '../../hooks/useCustomFields';
import EmailAttachmentBar from '../../components/EmailAttachmentBar';
// Voice-Clone bridge — only triggered when the selected topic is a
// Voice-Clone-mode template. Standard reply templates are unaffected.
import {
  isVoiceCloneTopicData,
  buildVoiceClonePayload,
  shouldGenerateSmartSubject,
  buildVoiceCloneSmartSubjectId,
  loadFreshContactRow,
} from '../CloneTemplate/buildVoiceClonePayload';
import { readVoiceCloneError, buildVoiceCloneError } from '../CloneTemplate/voiceCloneErrors';
import { accountTextSignature } from '../accountTextSignature';

const InboxModeEmail = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply, 
  source, 
  onEmailResult,
  userSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping
}) => {
  // 🔒 AUDIT: Hole die aktuelle User-ID für Team-Member-Tracking
  const { currentUserId } = useActiveWorld();

  // Pull the admin-defined custom-field schema for this workspace so the
  // Voice-Clone reply path can forward the recipient's custom-field values
  // to the prompt (Standard mode does the same via NormalModeEmail.js).
  const { contextFields } = useCustomFields(userId);

  const getFirstDmName = (data) => {
    try {
      if (data?.lead_decision_makers) {
        const dms = typeof data.lead_decision_makers === 'string' ? JSON.parse(data.lead_decision_makers) : data.lead_decision_makers;
        if (dms.length > 0 && dms[0].name) return dms[0].name.split(' ')[0];
      }
    } catch (e) {}
    return data?.lead_firstname || '';
  };
  
  // Detailliertes Logging der eingehenden Daten
  console.log(`📧 [INBOX-MODE] Component loaded with data:`, {
    source,
    isReply,
    hasOriginalEmail: !!rowData.original_email,
    hasDraft: !!rowData.draft,
    hasDraftOriginalEmail: !!rowData.draft?.originalEmail,
    rowData: rowData
  });

  if (rowData.draft) {
    console.log(`📧 [INBOX-MODE] Draft data details:`, {
      draftId: rowData.draft.id,
      agentName: rowData.draft.agentName,
      hasOriginalEmail: !!rowData.draft.originalEmail,
      originalEmailSubject: rowData.draft.originalEmail?.subject,
      originalEmailFrom: rowData.draft.originalEmail?.from,
      originalEmailContent: rowData.draft.originalEmail?.content,
      originalEmailBodyText: rowData.draft.originalEmail?.bodyText
    });
  }

  if (rowData.original_email) {
    console.log(`📧 [INBOX-MODE] Original email data:`, {
      subject: rowData.original_email.subject,
      from: rowData.original_email.from,
      content: rowData.original_email.content,
      date: rowData.original_email.date
    });
  }

  const [selectedTopic, setSelectedTopic] = useState("");
  // Style removed — defined in template
  const [contextInfo, setContextInfo] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPerfecting, setIsPerfecting] = useState(false);
  const [perfectSliderOpen, setPerfectSliderOpen] = useState(false);
  const [perfectCreativity, setPerfectCreativity] = useState(3);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [showBlockingOverlay, setShowBlockingOverlay] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const attachmentInputRef = useRef(null);

  // Email Tracking States. variantId is set only for Voice-Clone-mode replies
  // and rides along to the backend so per-structure-variant stats can be
  // credited. Standard sends leave it null.
  const [trackingData, setTrackingData] = useState({
    templateId: null,
    templateName: null,
    subjectLineId: null,
    instructionId: null,
    variantId: null,
  });
  
  // Ref für automatische Generierung
  const hasAutoGenerated = useRef(false);
  // Ref für das Betreff-Input-Element
  const subjectInputRef = useRef(null);
  // Ref für das Action-Menu
  const actionMenuRef = useRef(null);

  // Firebase Functions
  // markEmailAsReplied greift auf das Postfach zu, deductCredits gab es hier nie
  // in us-central1 — beide liegen in europe-west1.
  const functions = getFunctions(undefined, 'europe-west1');
  const markEmailAsReplied = httpsCallable(functions, 'markEmailAsReplied');

  // Erweiterte Funktion zum Markieren der ursprünglichen E-Mail als beantwortet
  const markOriginalEmailAsReplied = async (emailId, isFromDraft = false) => {
    if (!emailId || !userId) {
      console.warn(`📧 [MARK-REPLIED] Missing emailId or userId`);
      return;
    }
    
    try {
      console.log(`📧 [MARK-REPLIED] Marking original email ${emailId} as replied`);
      
      // ✅ NEU: Rufe NUR die Cloud-Funktion auf (kein RTDB Update mehr)
      // Die Cloud-Funktion markiert die E-Mail in Firestore als beantwortet
      
      let accountId = rowData.accountId;
      let isSpam = false;
      
      // Für Drafts: Ermittle den korrekten Account
      if (isFromDraft && rowData.draft) {
        accountId = rowData.draft.originalEmail?.accountId || rowData.accountId;
        isSpam = rowData.draft.originalEmailPath === 'spam';
      }
      
      // Rufe die Cloud-Funktion auf, um den Status in Firestore und auf dem E-Mail-Server zu aktualisieren
      await markEmailAsReplied({ 
        emailId: emailId, 
        accountId: accountId,
        isSpam: isSpam,
        userId: userId // 🔒 TEAM SUPPORT: Übergebe userId für Team-Mitglieder
      });
      
      console.log(`📧 [MARK-REPLIED] Successfully marked email ${emailId} as replied`);
      
    } catch (error) {
      console.error(`📧 [MARK-REPLIED] Error marking email ${emailId} as replied:`, error);
      // Fehler nicht weiterwerfen, da die E-Mail trotzdem erfolgreich gesendet wurde
    }
  };

  // Setze die E-Mail-Adresse des Empfängers
  useEffect(() => {
    // Für SafeGuard Retry: Verwende die gespeicherte Empfänger-Adresse
    if (rowData.isSafeGuardRetry && rowData.lead_email) {
      setSelectedEmail(rowData.lead_email);
      console.log(`🛡️ [SAFEGUARD-RETRY] Recipient email set:`, rowData.lead_email);
    }
    // Für Forward: Verwende die eingegebene E-Mail-Adresse
    else if (rowData.lead_type === "email_forward" && rowData.lead_email) {
      setSelectedEmail(rowData.lead_email);
      console.log(`🔄 [FORWARD] Recipient email set for forward:`, {
        forwardEmail: rowData.lead_email,
        leadType: rowData.lead_type
      });
    }
    // Für normale Reply: Verwende die Original-E-Mail From-Adresse
    else if (rowData.original_email && rowData.original_email.from) {
      // Extrahiere die E-Mail-Adresse aus dem From-Feld
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
      const match = rowData.original_email.from.match(emailRegex);
      
      if (match && match[1]) {
        setSelectedEmail(match[1]);
      }
    }
    // Für Drafts: Setze die E-Mail-Adresse aus der Original-E-Mail
    else if (rowData.draft && rowData.draft.originalEmail && rowData.draft.originalEmail.from) {
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
      const match = rowData.draft.originalEmail.from.match(emailRegex);
      
      if (match && match[1]) {
        setSelectedEmail(match[1]);
        console.log(`📝 [DRAFT-EMAIL] Email address set for draft:`, {
          draftId: rowData.draft.id,
          recipientEmail: match[1],
          originalFrom: rowData.draft.originalEmail.from
        });
      }
    }
  }, [rowData]);

  // Setze die Absender-E-Mail basierend auf dem Account, mit dem die E-Mail empfangen wurde
  useEffect(() => {
    if (rowData.accountEmail && senderEmails.length > 0) {
      const matchingEmail = senderEmails.find(email => email.email === rowData.accountEmail);
      if (matchingEmail) {
        setSelectedSenderEmail(matchingEmail.email);
      } else if (senderEmails.length > 0) {
        setSelectedSenderEmail(senderEmails[0].email);
      }
    } 
    // Für Drafts: Versuche die Account-E-Mail aus der Original-E-Mail zu ermitteln
    else if (rowData.draft && rowData.draft.originalEmail && senderEmails.length > 0) {
      // Prüfe, ob die Original-E-Mail Account-Informationen hat
      const originalAccountEmail = rowData.draft.originalEmail.accountEmail;
      if (originalAccountEmail) {
        const matchingEmail = senderEmails.find(email => email.email === originalAccountEmail);
        if (matchingEmail) {
          setSelectedSenderEmail(matchingEmail.email);
          console.log(`📝 [DRAFT-SENDER] Sender email set for draft:`, {
            draftId: rowData.draft.id,
            senderEmail: matchingEmail.email,
            originalAccountEmail: originalAccountEmail
          });
        } else if (senderEmails.length > 0) {
          setSelectedSenderEmail(senderEmails[0].email);
        }
      } else if (senderEmails.length > 0) {
        setSelectedSenderEmail(senderEmails[0].email);
      }
    }
    else if (senderEmails.length > 0) {
      setSelectedSenderEmail(senderEmails[0].email);
    }
  }, [rowData.accountEmail, rowData.draft, senderEmails]);

  // Initialisiere Kontext basierend auf dem Modus
  useEffect(() => {
    if (isReply && rowData) {
      if (source === 'draft' && rowData.draft && rowData.draft.originalEmail) {
        // Für Drafts: Zeige die E-Mail-Historie wie im Bulk-Modus
        const originalEmail = rowData.draft.originalEmail;
        const emailHistory = `
Original E-Mail:
Von: ${originalEmail.from || 'Unbekannt'}
Datum: ${new Date(originalEmail.date || Date.now()).toLocaleString()}
Betreff: ${originalEmail.subject || ''}

${originalEmail.content || originalEmail.bodyText || originalEmail.body || ''}

Draft erstellt von Agent: ${rowData.draft.agentName} (Case ${rowData.draft.agentCaseNumber})
Draft-Aufgabe: ${rowData.draft.agentTask || 'Keine Aufgabe definiert'}
        `.trim();
        
        setContextInfo(emailHistory);
      } else if (rowData.original_email) {
        // Für normale E-Mail-Antworten
        const originalEmail = rowData.original_email;
        const emailHistory = `
Original E-Mail:
Von: ${originalEmail.from || 'Unbekannt'}
Datum: ${new Date(originalEmail.timestamp || originalEmail.date || Date.now()).toLocaleString()}
Betreff: ${originalEmail.subject || ''}

${originalEmail.content || originalEmail.bodyText || originalEmail.body || ''}
        `.trim();
        
        setContextInfo(emailHistory);
      } else {
        // Fallback für andere Fälle
        const contextText = `
Business Name: ${rowData.business_name || ''}
Description: ${rowData.lead_description || ''}
        `.trim();
        setContextInfo(contextText);
      }
    } else {
      // Für neue E-Mails (nicht Antworten)
      const contextText = `
Business Name: ${rowData.business_name || ''}
City: ${rowData.lead_city || ''}
Description: ${rowData.lead_description || ''}
Type: ${rowData.lead_type || ''}
Website: ${rowData.lead_website || ''}
      `.trim();
      setContextInfo(contextText);
    }
  }, [isReply, rowData, source]);

  // Setze den Betreff für Antworten
  useEffect(() => {
    if (isReply && rowData) {
      let originalSubject = '';
      
      if (source === 'draft' && rowData.draft && rowData.draft.originalEmail) {
        originalSubject = rowData.draft.originalEmail.subject || '';
      } else if (rowData.original_email) {
        originalSubject = rowData.original_email.subject || '';
      }
      
      if (originalSubject) {
        // 🛡️ [SAFEGUARD-RETRY] Überprüfe preventReplyPrefix Flag für SafeGuard Retry
        if (rowData.preventReplyPrefix === true || rowData.isSafeGuardRetry === true) {
          console.log('🛡️ [SUBJECT] Using original subject without "Re:" for SafeGuard retry');
          setSubjectLine(originalSubject);
        } else {
          // Normale Reply-Logik mit "Re:" 
          const replySubject = originalSubject.startsWith('Re:') 
            ? originalSubject 
            : `Re: ${originalSubject}`;
          setSubjectLine(replySubject);
        }
      }
    }
  }, [isReply, rowData, source]);

  // Prüfe, ob es sich um einen Draft handelt und fülle den Inhalt vor
  useEffect(() => {
    if (rowData.draft && rowData.draft.content) {
      console.log(`📝 [DRAFT-MODE] Loading draft content:`, {
        draftId: rowData.draft.id,
        agentName: rowData.draft.agentName,
        contentLength: rowData.draft.content.length
      });
      
      // Setze den Draft-Inhalt direkt
      setBodyContent(rowData.draft.content);
      
      // Verhindere automatische Generierung für Drafts
      hasAutoGenerated.current = true;
    }
    // Für Forward: Verwende den vorgefüllten Content
    else if (rowData.prefilledBodyContent && rowData.skipGeneration) {
      console.log(`🔄 [FORWARD-MODE] Loading prefilled forward content:`, {
        contentLength: rowData.prefilledBodyContent.length,
        leadType: rowData.lead_type
      });
      
      // Setze den Forward-Inhalt direkt
      setBodyContent(rowData.prefilledBodyContent);
      
      // Verhindere automatische Generierung für Forward
      hasAutoGenerated.current = true;
    }
  }, [rowData.draft, rowData.prefilledBodyContent, rowData.skipGeneration]);

  // Laden der benutzerdefinierten Antwort-Topics
  useEffect(() => {
    if (!isTopicsLoaded) return;
    
    // Suche nach benutzerdefinierten Antwort-Topics
    const customReplyTopics = Object.entries(userTopics)
      .filter(([_, data]) => data.type === 'custom_reply')
      .map(([id, data]) => ({
        id,
        name: data.topic,
        type: data.type
      }));
    
    if (customReplyTopics.length > 0) {
      setSelectedTopic(customReplyTopics[0].name);
    } else {
      // Fallback auf Standard-Topics
      setSelectedTopic("Reply To Mail");
    }
  }, [isTopicsLoaded, userTopics]);

  // Setze das vorausgewählte Topic, wenn vorhanden
  useEffect(() => {
    if (rowData.selectedTopic) {
      setSelectedTopic(rowData.selectedTopic);
    }
  }, [rowData.selectedTopic]);

  // Automatische Generierung deaktiviert - User kann selbst Text eingeben
  // Hilfsfunktion für Standard-Topic-Anweisungen
  const getDefaultTopicNote = (topicType) => {
    const defaultNotes = {
      'custom_reply': "Bitte verfasse eine durchdachte Antwort auf die Nachricht des Empfängers. Gehe auf die konkreten Punkte ein.",
      'inbox_reply': "Verfasse eine höfliche und direkte Antwort auf die E-Mail. Gehe auf die wichtigsten Punkte ein und biete eine klare Lösung an.",
      'inbox_details': "Erstelle eine detaillierte E-Mail mit allen relevanten Informationen über unser Angebot. Erkläre die Vorteile und Preise.",
      'inbox_zoom': "Schlage ein Zoom-Meeting vor und biete mehrere mögliche Termine an. Erkläre kurz, was besprochen werden soll.",
      'inbox_meetup': "Schlage ein persönliches Treffen vor. Frage nach bevorzugten Orten und Zeiten.",
    };
    
    return defaultNotes[topicType] || "Bitte schreibe eine professionelle Antwort auf die E-Mail.";
  };

  // Hilfsfunktion für zufällige Auswahl aus Array (InboxMode)
  const getRandomFromArray = (array) => {
    if (!Array.isArray(array) || array.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
  };

  // Spezifische Anweisungen für den Antworttyp holen
  const getToneInstructionsForReplyType = (replyType) => {
    // Prüfe, ob es ein benutzerdefiniertes Topic mit diesem Typ gibt
    const customTopic = Object.entries(userTopics).find(([_, data]) => data.type === replyType);
    if (customTopic && customTopic[1]) {
      const topicData = customTopic[1];
      
      if (topicData.topicSpecificNotes && Array.isArray(topicData.topicSpecificNotes) && topicData.topicSpecificNotes.length > 0) {
        // Random selection — lead_pacing no longer drives variant selection.
        const randomInstruction = selectInstructionByPacing(topicData.topicSpecificNotes, null);
        console.log('🎲 [INBOX-RANDOM] Selected instruction:', randomInstruction);
        return randomInstruction;
      }
      // Backwards compatibility: Fallback auf einzelne Instruction
      else if (topicData.topicSpecificNote) {
        console.log('🎲 [INBOX-RANDOM] Single instruction found:', topicData.topicSpecificNote);
        return topicData.topicSpecificNote;
      }
    }
    
    // Fallback zu Standard-Anweisungen
    return getDefaultTopicNote(replyType);
  };

  // Topic-Typ basierend auf dem Namen ermitteln
  const getTopicType = (selectedTopicName) => {
    // Prüfe, ob es ein benutzerdefiniertes Topic ist
    const customTopic = Object.entries(userTopics).find(([_, data]) => data.topic === selectedTopicName);
    if (customTopic) {
      return customTopic[1].type;
    }
    
    // Mapping für Standard-Inbox-Topics
    const inboxTopicMapping = {
      "Reply To Mail": "inbox_reply",
      "Send Details": "inbox_details",
      "Schedule Zoom Call": "inbox_zoom",
      "Ask for Meetup": "inbox_meetup"
    };
    
    return inboxTopicMapping[selectedTopicName] || "custom_reply";
  };

  // E-Mail generieren
  const handleGenerate = async () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    setBodyContent("");
    
    try {
      // ===== VOICE-CLONE BRANCH =====
      // If the selected topic is a Voice-Clone-mode template, route through
      // the worldEmailCreator endpoint with `mode: 'voiceClone'` and skip the
      // entire standard reply pipeline. The backend loads the clone +
      // template fresh from RTDB and writes in the trained voice.
      const vcScopes = [
        Object.entries(userTopics || {}).filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && v.topic),
        userTopics?.selectedTopics ? Object.entries(userTopics.selectedTopics) : [],
        userTopics?.customTopics ? Object.entries(userTopics.customTopics) : [],
      ];
      let vcMatch = null;
      for (const entries of vcScopes) {
        const hit = entries.find(([, td]) => td?.topic === selectedTopic);
        if (hit) { vcMatch = { topicId: hit[0], topicData: hit[1] }; break; }
      }
      if (vcMatch && isVoiceCloneTopicData(vcMatch.topicData)) {
        // Re-read the contact so freshly-run research reaches the generator even
        // if the inbox snapshot is stale. Fail-safe: returns snapshot on error.
        const freshRow = await loadFreshContactRow(userId, rowData);
        const recipient = getRecipientInfoForContact(freshRow, selectedEmail);
        // Forward the same custom fields the rest of inbox mode resolves so
        // the Voice-Clone prompt sees what the contact card already shows
        // (instead of the previous hard-coded empty list).
        const vcCustomFieldsData = (contextFields || []).reduce((acc, cf) => {
          const val = freshRow?.[cf.key];
          if (val != null && val !== '') {
            acc.push({ key: cf.key, label: cf.label, value: String(val) });
          }
          return acc;
        }, []);

        const vcPayload = buildVoiceClonePayload({
          topicData: vcMatch.topicData,
          templateId: vcMatch.topicId,
          rowData: freshRow,
          userSettings,
          // Active workspace userId so the backend resolves the right RTDB
          // path when replying from inside a switched team workspace.
          userId,
          recipient,
          firstName: getFirstDmName(freshRow),
          customFieldsData: vcCustomFieldsData,
          // Inbox replies inherit the original subject in most cases — we
          // only ask the backend for a smart subject when the template
          // explicitly opted in. Reply-style "AW:/Re:" handling lives below.
          includeSubject: shouldGenerateSmartSubject(vcMatch.topicData),
          textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)),
        });
        if (!vcPayload) {
          alert('This CopyTwin template has no twin selected.');
          return;
        }

        // Credit deduction — priced centrally (config/creditPlans.js) so every
        // send path charges the same. Legacy 4-tier ids resolve inside.
        const vcTierId = vcMatch.topicData.cloneConfig?.qualityTier || 'best';
        const vcCreditCost = emailGenerationCost({ voiceCloneTier: vcTierId });
        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({
            action: 'voice_clone_send',
            amount: vcCreditCost,
            worldId: userId,
            metadata: {
              contactId: rowData?.id,
              templateId: vcMatch.topicId,
              cloneId: vcMatch.topicData.cloneConfig?.cloneId,
              qualityTier: vcTierId,
              source: 'inbox_reply',
            },
          });
        } catch (creditErr) {
          const msg = creditErr?.message || '';
          if (msg.includes('Not enough credits')) {
            alert(`Nicht genügend Credits für CopyTwin Antwort (${vcCreditCost} Credits erforderlich). Bitte lade deine Credits auf.`);
            return;
          }
          // Non-blocking: rate-limits or transient errors shouldn't kill
          // the generation. Mirrors the bulk-mode helper's lenient policy.
          console.warn('[VOICE-CLONE-INBOX] Credit deduction warning (non-blocking):', creditErr);
        }

        const vcResp = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(vcPayload),
        });
        if (!vcResp.ok) {
          // Parse the structured backend error so a fixable misconfiguration
          // (e.g. NO_ACTIVE_VARIANT) surfaces a clear, actionable message.
          const parsed = await readVoiceCloneError(vcResp);
          throw buildVoiceCloneError(parsed);
        }
        const vcData = await vcResp.json();
        setBodyContent(vcData.emailContent || '');
        // Only adopt the smart subject when the reply slot is empty (new
        // thread). If the user is replying to an existing thread we keep
        // the original "Re: ..." subject — and crucially, we DON'T mark
        // the smart bucket in stats since the smart subject never reached
        // the recipient.
        const usedSmartSubject =
          !!vcData.subjectLine && (!subjectLine || subjectLine.trim() === '');
        if (usedSmartSubject) {
          setSubjectLine(vcData.subjectLine);
        }
        setTrackingData({
          templateId: vcMatch.topicId,
          templateName: vcMatch.topicData.topic || vcMatch.topicData.type,
          subjectLineId: usedSmartSubject
            ? buildVoiceCloneSmartSubjectId(vcData.variantId)
            : null,
          instructionId: null,
          variantId: vcData.variantId || null,
        });
        console.log('🎯 [VOICE-CLONE-INBOX] Generated reply', { variantId: vcData.variantId });
        return;
      }
      // ===== END VOICE-CLONE BRANCH — standard pipeline below is unchanged =====

      // Für Antworten: Hole die spezifische Notiz für den ausgewählten Topic-Typ
      const topicType = getTopicType(selectedTopic);
      let topicSpecificNote = "";
      let topicGeneralInfo = '';
      let topicAiAdherence = 'exact';
      let topicFormattingStyle = 'as-written';
      let topicModel = null;
      let topicMaxTokens = null;
      let topicWritingLevel = 'middle_school';
      let topicAiTemperature = null;
      let topicHyperIntelligence = false;
      let topicPersonalizationGuide = '';
      let topicReferenceFile = null;
      let topicUltraMode = false;
      let topicSmartGreeting = false;
      let topicGreetingCasualPrefixes = ['Hey', 'Hi', 'Hello'];
      let topicGreetingFormalPrefixes = ['Dear'];
      let topicSmartSubjectLine = false;
      let topicSmartSubjectStyle = 'neutral';
      let topicSmartSubjectInstructions = '';
      let topicStaticSubjectLines = [];
      let topicLanguage = null;
      
      let resolvedTemplateId = null;
      let resolvedTemplateName = null;
      let resolvedInstructionId = null;
      let resolvedVariantPacing = null;

      const customTopic = Object.entries(userTopics).find(([_, data]) => data.topic === selectedTopic);
      if (customTopic && customTopic[1]) {
        const [topicId, topicData] = customTopic;
        resolvedTemplateId = topicId;
        resolvedTemplateName = topicData.topic || topicData.type;
        
        topicGeneralInfo = topicData.generalInfo || '';
        topicAiAdherence = topicData.aiAdherence || 'exact';
        topicFormattingStyle = topicData.formattingStyle || 'as-written';
        topicModel = topicData.model || null;
        topicMaxTokens = topicData.maxTokens || null;
        topicWritingLevel = topicData.writingLevel || 'middle_school';
        topicAiTemperature = topicData.aiTemperature != null ? topicData.aiTemperature : null;
        topicHyperIntelligence = topicData.hyperIntelligence || false;
        topicPersonalizationGuide = topicData.personalizationGuide || '';
        topicReferenceFile = topicData.referenceFile || null;
        topicUltraMode = topicData.ultraMode || false;
        topicSmartGreeting = topicData.smartGreeting || false;
        topicGreetingCasualPrefixes = Array.isArray(topicData.greetingCasualPrefixes) ? topicData.greetingCasualPrefixes : (Array.isArray(topicData.greetingPrefixes) ? topicData.greetingPrefixes : ['Hey', 'Hi', 'Hello']);
        topicGreetingFormalPrefixes = Array.isArray(topicData.greetingFormalPrefixes) ? topicData.greetingFormalPrefixes : ['Dear'];
        topicSmartSubjectLine = topicData.smartSubjectLine || false;
        topicSmartSubjectStyle = topicData.smartSubjectStyle || 'neutral';
        topicSmartSubjectInstructions = topicData.smartSubjectInstructions || '';
        topicStaticSubjectLines = Array.isArray(topicData.subjectLines) ? topicData.subjectLines : [];
        topicLanguage = topicData.language || null;
        
        if (topicData.topicSpecificNotes && Array.isArray(topicData.topicSpecificNotes) && topicData.topicSpecificNotes.length > 0) {
          // Random selection — lead_pacing no longer used; greeting tone is
          // driven by the selected variant's own pacing.
          const randomInstruction = selectInstructionByPacing(topicData.topicSpecificNotes, null);
          console.log('🎲 [INBOX-RANDOM] Selected instruction in generate:', randomInstruction);
          resolvedInstructionId = extractItemId(randomInstruction);
          resolvedVariantPacing = (randomInstruction && typeof randomInstruction === 'object') ? (randomInstruction.pacing || null) : null;
          topicSpecificNote = extractItemText(randomInstruction) || randomInstruction;
        }
        // Backwards compatibility: Fallback auf einzelne Instruction
        else if (topicData.topicSpecificNote) {
          console.log('🎲 [INBOX-RANDOM] Single instruction found in generate:', topicData.topicSpecificNote);
          resolvedInstructionId = extractItemId(topicData.topicSpecificNote) || `legacy_${topicId}`;
          topicSpecificNote = extractItemText(topicData.topicSpecificNote) || topicData.topicSpecificNote;
        }
      }

      // Also check selectedTopics and customTopics sub-objects
      if (!resolvedTemplateId) {
        const searchIn = [userTopics.selectedTopics, userTopics.customTopics].filter(Boolean);
        for (const topics of searchIn) {
          const found = Object.entries(topics).find(([_, data]) => data.topic === selectedTopic);
          if (found) {
            const [topicId, topicData] = found;
            resolvedTemplateId = topicId;
            resolvedTemplateName = topicData.topic || topicData.type;
            topicGeneralInfo = topicData.generalInfo || '';
            topicAiAdherence = topicData.aiAdherence || 'exact';
            topicFormattingStyle = topicData.formattingStyle || 'as-written';
            topicModel = topicData.model || null;
            topicMaxTokens = topicData.maxTokens || null;
            topicWritingLevel = topicData.writingLevel || 'middle_school';
            topicAiTemperature = topicData.aiTemperature != null ? topicData.aiTemperature : null;
            topicHyperIntelligence = topicData.hyperIntelligence || false;
            topicPersonalizationGuide = topicData.personalizationGuide || '';
            topicReferenceFile = topicData.referenceFile || null;
            topicUltraMode = topicData.ultraMode || false;
            topicSmartGreeting = topicData.smartGreeting || false;
            topicGreetingCasualPrefixes = Array.isArray(topicData.greetingCasualPrefixes) ? topicData.greetingCasualPrefixes : ['Hey', 'Hi', 'Hello'];
            topicGreetingFormalPrefixes = Array.isArray(topicData.greetingFormalPrefixes) ? topicData.greetingFormalPrefixes : ['Dear'];
            topicSmartSubjectLine = topicData.smartSubjectLine || false;
            topicSmartSubjectStyle = topicData.smartSubjectStyle || 'neutral';
            topicSmartSubjectInstructions = topicData.smartSubjectInstructions || '';
            topicStaticSubjectLines = Array.isArray(topicData.subjectLines) ? topicData.subjectLines : [];
            topicLanguage = topicData.language || null;
            break;
          }
        }
      }

      setTrackingData({
        templateId: resolvedTemplateId,
        templateName: resolvedTemplateName,
        subjectLineId: null,
        instructionId: resolvedInstructionId
      });
      console.log('📧 [INBOX-TRACKING] Tracking data set:', { templateId: resolvedTemplateId, templateName: resolvedTemplateName, instructionId: resolvedInstructionId });
      
      // Wenn keine benutzerdefinierte Notiz gefunden wurde, verwende die Standard-Anweisungen
      if (!topicSpecificNote) {
        const fallbackNote = getToneInstructionsForReplyType(topicType);
        if (fallbackNote && typeof fallbackNote === 'object') {
          resolvedInstructionId = resolvedInstructionId || extractItemId(fallbackNote);
          topicSpecificNote = extractItemText(fallbackNote) || fallbackNote;
        } else {
          topicSpecificNote = fallbackNote;
        }

        // Resolve tracking from type-based search if name-based search missed
        if (!resolvedTemplateId) {
          const typeTopic = Object.entries(userTopics).find(([_, data]) => data.type === topicType);
          if (typeTopic) {
            resolvedTemplateId = typeTopic[0];
            resolvedTemplateName = typeTopic[1].topic || typeTopic[1].type;
          }
        }

        setTrackingData(prev => ({
          ...prev,
          templateId: prev.templateId || resolvedTemplateId,
          templateName: prev.templateName || resolvedTemplateName,
          instructionId: prev.instructionId || resolvedInstructionId
        }));
      }

      // Ensure topicSpecificNote is always a string for the API
      if (topicSpecificNote && typeof topicSpecificNote === 'object') {
        topicSpecificNote = extractItemText(topicSpecificNote) || JSON.stringify(topicSpecificNote);
      }
      
      // Per-template model/language config with global fallback
      const model = topicModel || aiSettings.model || 'gpt-4.1-mini';
      const maxTokens = topicMaxTokens || parseInt(aiSettings.max_tokens || aiSettings.maxTokens) || 500;
      const language = topicLanguage || aiSettings.language || 'auto';

      const allEmails = getAllContactEmails(rowData);
      const recipient = getRecipientInfoForContact(rowData, selectedEmail);
      const isDm = allEmails.some(e => e.email === selectedEmail && e.source === 'decision_maker');
      const effectivePacing = getEffectivePacing(rowData?.lead_pacing || null, isDm);

      const requestData = {
        topic: selectedTopic,
        context: contextInfo,
        businessName: rowData.business_name || '',
        website: rowData.lead_website || '',
        senderName: userSettings.user_name || '',
        senderEmail: userSettings.user_email || '',
        textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)),
        firstName: getFirstDmName(rowData),
        recipientName: recipient.name,
        recipientRole: recipient.role,
        topicSpecificNote: topicSpecificNote,
        model,
        maxTokens,
        language,
        // Template-specific controls (per template)
        generalInfo: topicGeneralInfo,
        aiAdherence: topicAiAdherence,
        formattingStyle: topicFormattingStyle,
        writingLevel: topicWritingLevel || 'middle_school',
        ...(topicAiTemperature != null ? { aiTemperature: topicAiTemperature } : {}),
        hyperIntelligence: topicHyperIntelligence,
        personalizationGuide: topicPersonalizationGuide,
        referenceFile: topicReferenceFile || null,
        ultraMode: topicUltraMode || false,
        smartGreeting: topicSmartGreeting,
        greetingCasualPrefixes: topicGreetingCasualPrefixes,
        greetingFormalPrefixes: topicGreetingFormalPrefixes,
        greetingTone: greetingToneFromVariant(resolvedVariantPacing),
        smartSubjectLine: topicSmartSubjectLine,
        smartSubjectStyle: topicSmartSubjectStyle,
        smartSubjectInstructions: [aiSettings.subjectLineGuidelines, topicSmartSubjectInstructions].filter(Boolean).join('\n') || '',
        existingSubjects: [],
        leadPacing: effectivePacing,
        ...(() => {
          const match = (topicStaticSubjectLines || []).find(sl => hasInstructionBlocks(typeof sl === 'object' ? sl.text : sl));
          if (match) {
            let tmpl = typeof match === 'object' ? match.text : match;
            tmpl = tmpl.replace(/{{\s*business_name\s*}}/g, rowData.business_name || '')
                       .replace(/{{\s*name\s*}}/g, getFirstDmName(rowData) || '')
                       .replace(/{{\s*company\s*}}/g, rowData.business_name || '')
                       .replace(/{{\s*recipient_name\s*}}/g, recipient.name)
                       .replace(/{{\s*recipient_role\s*}}/g, recipient.role);
            return { subjectLineTemplate: tmpl };
          }
          return {};
        })(),
      };

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`E-Mail-Generierung fehlgeschlagen: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setBodyContent(data.emailContent);
      
      if (data.smartSubjectLine) {
        const validStaticLines = (topicStaticSubjectLines || []).filter(sl => {
          const text = typeof sl === 'object' ? sl.text : sl;
          return text && text.trim();
        });

        if (validStaticLines.length > 0) {
          const pool = [
            { text: data.smartSubjectLine, id: 'smart_subject' },
            ...validStaticLines
              .filter(sl => {
                const raw = typeof sl === 'object' ? sl.text : sl;
                return !hasInstructionBlocks(raw);
              })
              .map(sl => {
                let text = typeof sl === 'object' ? sl.text : sl;
                text = text.replace(/{{business_name}}/g, rowData?.business_name || '')
                          .replace(/{{name}}/g, getFirstDmName(rowData) || '')
                          .replace(/{{company}}/g, rowData?.business_name || '');
                return { text, id: typeof sl === 'object' ? sl.id : null };
              })
          ];
          if (data.resolvedSubjectLine) {
            pool.push({ text: data.resolvedSubjectLine, id: 'resolved_subject' });
          }
          const pick = pool[Math.floor(Math.random() * pool.length)];
          console.log(`🎲 [SUBJECT-POOL] Pool size: ${pool.length} (1 smart + static + resolved), picked:`, pick);
          setSubjectLine(pick.text);
          setTrackingData(prev => ({ ...prev, subjectLineId: pick.id }));
        } else {
          setSubjectLine(data.smartSubjectLine);
          setTrackingData(prev => ({ ...prev, subjectLineId: 'smart_subject' }));
        }
        if (data.resolvedSubjectAlternatives && data.resolvedSubjectAlternatives.length > 0) {
          // Alternatives available for future use
        }
      } else if (data.resolvedSubjectLine) {
        console.log('📝 [SUBJECT-INSTR] Using resolved subject from backend:', data.resolvedSubjectLine);
        setSubjectLine(data.resolvedSubjectLine);
        setTrackingData(prev => ({ ...prev, subjectLineId: 'resolved_subject' }));
      } else if ((!subjectLine || subjectLine.trim() === '') && data.subjectLine) {
        setSubjectLine(data.subjectLine);
      }
    } catch (error) {
      console.error('Fehler bei der E-Mail-Generierung:', error);
      // Prefer the Voice-Clone user-facing message (e.g. "no active variant")
      // so the user knows exactly what to fix in the template editor.
      alert(error?.vcUserMessage || 'Die E-Mail konnte nicht generiert werden. Bitte versuche es erneut.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Build creativity-dependent prompt for perfect mode
  const getPerfectPrompt = (level) => {
    if (level <= 2) return `Take the existing email content and only fix typos, grammar errors, and minor phrasing issues. Keep the text almost identical — same structure, same words, same tone. Only correct obvious mistakes.`;
    if (level <= 4) return `Take the existing email content and polish it. Fix any errors, improve word choices, and enhance clarity while keeping the same structure, tone, and core message. Small refinements only — the email should still feel very similar to the original.`;
    if (level <= 6) return `Take the existing email content and improve it noticeably. Rephrase sentences for better flow, enhance professionalism, and make it more engaging. Keep the core message and intent, but feel free to restructure sentences and improve the overall quality significantly.`;
    if (level <= 8) return `Take the existing email content and substantially rewrite it. Use the same key information and intent, but create a fresh, compelling version with new phrasing, better structure, and a more engaging approach. The result should feel like a new email covering the same points.`;
    return `Use the existing email only as a source of information (recipient, topic, intent). Write a completely new, creative email from scratch based on these details. New structure, new angle, new wording — make it stand out and feel entirely fresh.`;
  };

  // E-Mail perfektionieren (bestehenden Text verfeinern)
  const handlePerfectEmail = async (creativityLevel) => {
    if (isPerfecting || !bodyContent || bodyContent.trim() === "") {
      console.log('📧 [INBOX-PERFECT] Cannot perfect - already perfecting or no content');
      return;
    }
    
    const level = creativityLevel ?? perfectCreativity;
    setIsPerfecting(true);
    setBodyContent(''); // Text sofort entfernen für Loading-Animation
    
    try {
      console.log('📧 [INBOX-PERFECT] Perfecting email for:', rowData.business_name, '| creativity:', level);
      
      // KI-Modellparameter — Template-spezifische Settings bevorzugen (wie bei normaler Generierung)
      const perfectTemplate = Object.values(userTopics || {}).find(t => t.topic === selectedTopic);
      const model = perfectTemplate?.model || aiSettings.model || 'gpt-4.1-mini';
      const maxTokens = perfectTemplate?.maxTokens || parseInt(aiSettings.max_tokens || aiSettings.maxTokens) || 500;
      const language = perfectTemplate?.language || aiSettings.language || 'auto';
      
      const requestData = {
        topic: "Perfect Email",
        context: `Current email content: ${bodyContent}`,
        businessName: rowData.business_name || '',
        firstName: getFirstDmName(rowData),
        senderName: userSettings.user_name || '',
        senderEmail: selectedSenderEmail,
        textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)),
        topicSpecificNote: getPerfectPrompt(level),
        model,
        maxTokens,
        language,
        perfectMode: true
      };
      
      // API-Call zur Perfektionierung
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        throw new Error(`API-Fehler: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.emailContent) {
        // Setze den perfektionierten Inhalt
        setBodyContent(data.emailContent);
        
        console.log('📧 [INBOX-PERFECT] ✅ Email successfully perfected');
      } else {
        throw new Error('Unerwartetes API-Antwortformat');
      }
      
    } catch (error) {
      console.error('📧 [INBOX-PERFECT] Error perfecting email:', error);
      // Bei Fehler bleibt der ursprüngliche Text erhalten
    } finally {
      setIsPerfecting(false);
    }
  };

  // Letzten Absender in der Datenbank speichern
  const saveLastSenderToDatabase = async (senderEmail) => {
    try {
      if (!userId || !senderEmail) return false;
      
      const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
      await update(lastSenderRef, {
        email: senderEmail,
        timestamp: Date.now(),
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Fehler beim Speichern des letzten Absenders:', error);
      return false;
    }
  };

  // E-Mail senden
  const handleSend = async () => {
    if (!selectedEmail) {
      onEmailResult('error', 'No recipient email address provided');
      return;
    }

    // 📎 Attachments must be fully uploaded before we hand off to the backend.
    if (EmailAttachmentBar.hasPending(attachments)) {
      onEmailResult('error', 'Please wait for attachments to finish uploading.');
      return;
    }
    if (EmailAttachmentBar.hasErrors(attachments)) {
      onEmailResult('error', 'One or more attachments failed to upload. Remove them or retry.');
      return;
    }

    const emailId = rowData.id;
    const currentContent = bodyContent;
    const currentSubject = subjectLine;
    const currentSelectedEmail = selectedEmail;
    const currentSenderEmail = selectedSenderEmail;
    
    // 🔄 [FORWARD-FIX] Prüfe, ob es sich um eine Forward-E-Mail handelt
    const isForwardEmail = rowData.lead_type === "email_forward" || 
      rowData.isForwardOperation === true || 
      (emailId && (emailId.toString().startsWith('forward_') || emailId.toString().startsWith('temp_forward_')));
    
    console.log(`📧 [FORWARD-DEBUG] Email type check:`, {
      isForwardEmail,
      leadType: rowData.lead_type,
      isForwardOperation: rowData.isForwardOperation,
      emailId: emailId,
      startsWithForward: emailId && emailId.toString().startsWith('forward_'),
      startsWithTempForward: emailId && emailId.toString().startsWith('temp_forward_')
    });
    
    setIsSending(true);
    
    // E-Mail im Hintergrund senden
    (async () => {
      try {
        // Absender-E-Mail finden
        const senderEmailData = senderEmails.find(item => item.email === currentSenderEmail);
        
        if (!senderEmailData) {
          throw new Error("The selected sender email was not found.");
        }

        // Resolve gegen stale React-State (Race zwischen Firestore/RTDB-Listenern).
        // Bei Read-Error fällt der Helper transparent auf den bestehenden State zurück.
        const { footerId: resolvedFooterId, footerSettings: resolvedFooterSettings } = await resolveFooterForSend({
          userId,
          senderAccountId: senderEmailData.id,
          fallbackFooterId: senderEmailData.footerId || null,
          fallbackFooterSettings: userSettings,
        });
        
        // E-Mail formatieren. Alt-Texte pro Empfänger auflösen, damit kein
        // identisches / platzhalter-leakendes alt-Attribut versendet wird.
        const settingsForHtml = withResolvedImageAltText(
          resolvedFooterSettings,
          buildImagePromptVariables(rowData, { userSettings, selectedEmail })
        );
        const formattedContent = convertToHtml(currentContent, settingsForHtml, resolvedFooterId);
        
        // Detailliertes Logging der userSettings vor dem Footer-Hinzufügen
        console.log(`📧 [FOOTER-DEBUG] userSettings before addFooterToEmail:`, {
          hasResolvedSettings: !!resolvedFooterSettings,
          resolvedFooterId,
          footerText: resolvedFooterSettings?.footer_text,
          footerImageUrl: resolvedFooterSettings?.footer_image_url,
          footerFont: resolvedFooterSettings?.footer_font,
          footerImagePosition: resolvedFooterSettings?.footer_image_position,
          footerImageSize: resolvedFooterSettings?.footer_image_size,
        });
        
        let contentWithFooter = addFooterToEmail(formattedContent, resolvedFooterSettings, resolvedFooterId);
        
        console.log(`📧 [FOOTER-DEBUG] Content before footer:`, formattedContent?.substring(0, 200));
        console.log(`📧 [FOOTER-DEBUG] Content after footer:`, contentWithFooter?.substring(0, 500));
        
        // Füge die Konversationshistorie hinzu (nicht für SafeGuard Retries — das sind Cold Emails)
        if (rowData.original_email && !rowData.isSafeGuardRetry) {
          const originalEmail = rowData.original_email;
          
          const emailHistory = `
<div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #666;">
  <p style="margin-bottom: 10px; color: #888; font-style: italic;">Am ${new Date(originalEmail.date || originalEmail.timestamp || Date.now()).toLocaleString()} schrieb ${originalEmail.from || 'Unbekannt'}:</p>
  <blockquote style="margin: 0; padding-left: 10px; border-left: 2px solid #ddd; color: #666;">
    ${originalEmail.content || originalEmail.body || ''}
  </blockquote>
</div>
          `;
          
          contentWithFooter = `${contentWithFooter}\n\n${emailHistory}`;
        }
        
        // Request-Daten erstellen
        const isSafeGuardRetry = rowData.isSafeGuardRetry === true;
        const requestData = {
          recipients: [currentSelectedEmail],
          subject: currentSubject || '',
          content: contentWithFooter || '',
          userId: userId,
          businessName: isForwardEmail
            ? (senderEmailData.sender_name || userSettings.user_name || 'Forward')
            : isSafeGuardRetry
              ? (rowData.business_name || 'SafeGuard Retry')
              : "E-Mail-Antwort",
          leadId: isForwardEmail ? null : (rowData.leadId || (isSafeGuardRetry ? (rowData.original_email?.originalEmailId || emailId) : emailId)),
          emailType: isForwardEmail ? "forward" : (isSafeGuardRetry ? 'safeguard_retry' : getTopicType(selectedTopic)),
          senderEmail: senderEmailData.email.trim(),
          senderPassword: senderEmailData.password,
          senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
          imapHost: senderEmailData.imap_host,
          imapPort: senderEmailData.imap_port,
          smtpHost: senderEmailData.smtp_host,
          smtpPort: senderEmailData.smtp_port,
          encryptionType: senderEmailData.encryption_type,
          smtpEncryption: String(senderEmailData.smtp_encryption),
          emailId: senderEmailData.id,
          forceImmediate: isSafeGuardRetry ? true : true,
          isForward: isForwardEmail,
          isReply: isSafeGuardRetry ? false : !isForwardEmail,
          queueName: isSafeGuardRetry ? 'world-email-queue' : (!isForwardEmail ? 'reply-queue' : 'world-email-queue'),
          // 📎 ATTACHMENTS: only metadata; backend pulls bytes from Storage right before send
          attachments: EmailAttachmentBar.toRequestPayload(attachments),
          // 📧 EMAIL TRACKING: Send tracking data to backend
          templateId: trackingData.templateId,
          subjectLineId: trackingData.subjectLineId,
          instructionId: trackingData.instructionId,
          // Voice-Clone-mode replies carry a variantId so the backend can
          // credit per-structure-variant stats. Standard sends pass null.
          variantId: trackingData.variantId || null,
          topicName: selectedTopic,
          sentByUserId: currentUserId
        };
        
        console.log(`🔄 [FORWARD-DEBUG] Request data:`, {
          isForward: requestData.isForward,
          leadId: requestData.leadId,
          emailType: requestData.emailType,
          businessName: requestData.businessName
        });
        
        // E-Mail senden
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        const responseText = await response.text();
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Fehler beim Parsen der JSON-Antwort:', e);
          if (onEmailResult) {
            onEmailResult('error', `Server response is not valid JSON: ${responseText.substring(0, 100)}...`, rowData);
          }
          return;
        }
        
        if (data.success) {
          // 🛡️ [SAFEGUARD] Prüfe, ob E-Mail von SafeGuard abgelehnt und als Draft gespeichert wurde
          if (data.drafted && data.safeGuardRating !== undefined) {
            // SafeGuard hat die E-Mail abgelehnt und als Draft gespeichert
            if (onEmailResult) {
              onEmailResult(
                'safeguard_rejected', 
                `Email quality rating: ${data.safeGuardRating}/${data.minimumRating || 10}. Saved as draft for manual review.`, 
                rowData, 
                null, 
                currentSelectedEmail
              );
            }
            return;
          }

          // 📊 [PRE-SEND SCORER] Scored but blocked in strict mode
          if (data.drafted && data.preSendScore) {
            if (onEmailResult) {
              const s = data.preSendScore;
              onEmailResult(
                'scorer_rejected',
                `Pre-Send Score: ${s.compositeScore}/10 (Buyer: ${s.buyerValue}/10, Deliverability: ${s.deliverabilityRisk}/10). Saved as draft.`,
                rowData,
                null,
                currentSelectedEmail
              );
            }
            return;
          }
          
          // SafeGuard Retry: lösche den Draft aus Firestore nach erfolgreichem Senden
          if (isSafeGuardRetry && rowData.original_email?.originalEmailId) {
            try {
              const { doc: firestoreDoc, deleteDoc } = await import('firebase/firestore');
              const draftDocRef = firestoreDoc(firestore, 'world_drafts', userId, 'emails', rowData.original_email.originalEmailId);
              await deleteDoc(draftDocRef);
              console.log('🛡️ [SAFEGUARD-RETRY] Draft deleted from Firestore after successful send');
            } catch (deleteErr) {
              console.warn('🛡️ [SAFEGUARD-RETRY] Could not delete draft:', deleteErr.message);
            }
          }

          // Erfolg — UI sofort aktualisieren, Background-Tasks danach
          setSendSuccess(true);

          if (onEmailResult) {
            if (data.delayed && data.scheduledTime) {
              let scheduledTimeStamp = null;
              try {
                scheduledTimeStamp = new Date(data.scheduledTime).getTime();
              } catch (e) {
                console.error("Fehler bei der Konvertierung der geplanten Zeit:", e);
              }
              
              onEmailResult('success', isForwardEmail ? `Forward scheduled for later` : isSafeGuardRetry ? `Email sent successfully` : `Reply scheduled for later`, rowData, scheduledTimeStamp, currentSelectedEmail, currentSenderEmail);
            } else {
              onEmailResult('success', isForwardEmail ? `Email successfully forwarded` : isSafeGuardRetry ? `Email sent successfully` : `Reply successfully sent`, rowData, null, currentSelectedEmail, currentSenderEmail);
            }
          }

          setTimeout(() => {
            if (onClose) {
              onClose(true);
            }
          }, 1200);

          // Background-Tasks — nicht UI-blockierend
          saveLastSenderToDatabase(currentSenderEmail).catch(() => {});
          
          if (rowData.id && !isForwardEmail) {
            setAiTagAfterEmailSend(selectedTopic, rowData.id).catch(() => {});
          }
          
          if (!isForwardEmail) {
            if (rowData.draft && rowData.draft.originalEmailId) {
              markOriginalEmailAsReplied(rowData.draft.originalEmailId, true).catch(() => {});
            } else if (rowData.id) {
              markOriginalEmailAsReplied(rowData.id, false).catch(() => {});
            }
          }
          
          if (rowData.draft && rowData.draft.id && !isForwardEmail) {
            const draftRef = ref(database, `/World/${userId}/emailDrafts/${rowData.draft.id}`);
            update(draftRef, {
              status: 'sent',
              sentAt: new Date().toISOString(),
              sentTimestamp: Date.now(),
              sentTo: currentSelectedEmail,
              sentSubject: currentSubject
            }).catch(() => {});
          }
          
        } else {
          if (data.optOutBlocked) {
            if (onEmailResult) {
              onEmailResult('blocked', `Contact is blocked or opted out. ${data.error || ''}`, rowData);
            }
            return;
          }

          // CRM Domain Guard blocked — see NormalModeEmail for full rationale.
          if (data.blocked === true && data.blockType === 'crm_guard') {
            const sourceLabel = data.crmLeadSourceValue
              ? `Lead Source "${data.crmLeadSourceValue}"`
              : (data.crmStatus === 'crm_found_empty_source' ? 'empty Lead Source' : 'foreign Lead Source');
            const crmMessage = `Already in Close CRM (${sourceLabel}) — outreach blocked.`;
            if (onEmailResult) {
              onEmailResult('blocked', crmMessage, rowData);
            }
            return;
          }

          // Fehler
          let errorMessage = data.error || 'Unknown error sending email';
          
          if (data.error && data.error.includes("Sender address rejected")) {
            errorMessage = `The email address "${senderEmailData.email}" was rejected by the server.`;
          }
          
          if (onEmailResult) {
            onEmailResult('error', errorMessage, rowData);
          }
        }
      } catch (error) {
        console.error("Error sending email:", error);
        
        if (onEmailResult) {
          onEmailResult('error', error.message || 'Unknown error sending email', rowData);
        }
      } finally {
        setIsSending(false);
      }
    })();
  };

  // Direktes Aktualisieren des Betreffs
  const updateSubjectInputDirectly = (subject) => {
    setSubjectLine(subject);
    
    if (subjectInputRef.current) {
      subjectInputRef.current.value = subject;
      
      const event = new Event('input', { bubbles: true });
      subjectInputRef.current.dispatchEvent(event);
    }
  };

  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  const isEmailValid = () => {
    return (
      selectedEmail && 
      selectedEmail.trim() !== '' && 
      selectedSenderEmail && 
      selectedSenderEmail.trim() !== '' && 
      subjectLine && 
      subjectLine.trim() !== '' && 
      bodyContent && 
      bodyContent.trim() !== ''
    );
  };

  // 🔥 NEUE FUNKTION: AI Tag basierend auf Topic nach E-Mail-Versand setzen
  const setAiTagAfterEmailSend = async (topic, contactId) => {
    try {
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] Checking if topic has associated AI Tag:', topic);
      
      if (!userId || !contactId || !topic) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Missing required parameters');
        return false;
      }
      
      // Erweiterte Suche: Prüfe sowohl in selectedTopics als auch in customTopics
      let matchingTopic = null;
      
      // 1. Suche in allen userTopics (sowohl selectedTopics als auch customTopics)
      matchingTopic = Object.values(userTopics).find(topicData => {
        return topicData.topic === topic || topicData.type === topic;
      });
      
      // 2. Falls nicht gefunden, erweiterte Suche in der Struktur
      if (!matchingTopic && userTopics.selectedTopics) {
        matchingTopic = Object.values(userTopics.selectedTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      // 3. Falls immer noch nicht gefunden, suche in customTopics
      if (!matchingTopic && userTopics.customTopics) {
        matchingTopic = Object.values(userTopics.customTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      if (!matchingTopic) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] No matching topic found in userTopics for:', topic);
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Available topics:', {
          allTopics: Object.values(userTopics).map(t => ({ topic: t.topic, type: t.type, hasAiTagId: !!t.aiTagId })),
          selectedTopics: userTopics.selectedTopics ? Object.keys(userTopics.selectedTopics) : 'none',
          customTopics: userTopics.customTopics ? Object.keys(userTopics.customTopics) : 'none'
        });
        return false;
      }
      
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] Found matching topic:', matchingTopic);
      
      // Prüfe, ob das Topic einen zugeordneten AI Tag hat
      if (!matchingTopic.aiTagId) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Topic has no associated AI tag - this is normal');
        return false;
      }
      
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] ✅ Topic has associated AI tag ID:', matchingTopic.aiTagId);
      
      // Lade die AI Tag-Einstellungen
      const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${matchingTopic.aiTagId}`);
      const aiTagSnapshot = await new Promise((resolve) => {
        onValue(aiTagRef, resolve, { onlyOnce: true });
      });
      
      if (!aiTagSnapshot.exists()) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] AI tag not found for ID:', matchingTopic.aiTagId);
        return false;
      }
      
      const aiTagData = aiTagSnapshot.val();
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] AI tag data loaded:', aiTagData);
      
      // Erstelle den neuen Tag für den Kontakt
      const newTag = {
        tag: aiTagData.tag,
        name: aiTagData.name,
        color: aiTagData.color,
        icon: aiTagData.icon,
        appliedAt: new Date().toISOString(),
        appliedBy: 'post_email_send_reply',
        appliedTopic: topic,
        ...(aiTagData.selectedTopic && { selectedTopic: aiTagData.selectedTopic })
      };
      
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);

      if (aiTagData.overwriteOtherTags === true) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Overwrite tag detected, removing all other tags');
        await updateDoc(contactDocRef, { tags: { [matchingTopic.aiTagId]: newTag } });
      } else {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Adding tag alongside existing tags');
        const contactSnap = await getDoc(contactDocRef);
        const existingTags = contactSnap.exists() ? contactSnap.data().tags || {} : {};
        await updateDoc(contactDocRef, { [`tags.${matchingTopic.aiTagId}`]: newTag, tags: { ...existingTags, [matchingTopic.aiTagId]: newTag } });
      }
      
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] ✅ AI tag successfully applied after reply send');
      return true;
      
    } catch (error) {
      console.error('🏷️ [INBOX-POST-EMAIL-TAG] Error setting AI tag after reply send:', error);
      return false;
    }
  };

  // Calendly-Link öffnen
  const handleOpenCalendly = () => {
    if (userSettings.calendlyBookingLink) {
      window.open(userSettings.calendlyBookingLink, '_blank');
    }
    setIsActionMenuOpen(false); // Menu nach Aktion schließen
  };

  // Calendly-Link kopieren
  const handleCopyCalendlyLink = async () => {
    if (userSettings.calendlyBookingLink) {
      try {
        await navigator.clipboard.writeText(userSettings.calendlyBookingLink);
        // Optionally show a success message
      } catch (err) {
        console.error('Failed to copy Calendly link:', err);
      }
    }
    setIsActionMenuOpen(false); // Menu nach Aktion schließen
  };

  // Action Menu togglen
  const toggleActionMenu = () => {
    setIsActionMenuOpen(!isActionMenuOpen);
  };

  // Action ausführen und Menu schließen
  const handlePerfectAndClose = () => {
    setPerfectSliderOpen(true);
    setIsActionMenuOpen(false);
  };

  const handleGenerateAndClose = () => {
    handleGenerate();
    setIsActionMenuOpen(false);
  };

  // Click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setIsActionMenuOpen(false);
      }
    };

    if (isActionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionMenuOpen]);

  return (
    <div className="emailAiPopupContainer reply-mode">
      <div className="emailAiPopupHeader">
        <h2>
          {rowData.draft ? (
            <>
              📝 Draft bearbeiten
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 'normal', 
                color: '#666', 
                marginLeft: '10px' 
              }}>
                von {rowData.draft.agentName}
              </span>
            </>
          ) : (
            'E-Mail-Antwort'
          )}
        </h2>
        <button className="emailAiPopupCloseButton" onClick={onClose}>×</button>
      </div>
      
      <div className="emailAiPopupContent">
        {/* LINKE SPALTE: Topics, Style, Context */}
        <div className="emailAiPopupLeftColumn">
          {/* Draft Info Section - nur anzeigen wenn es ein Draft ist */}
          {source === 'draft' && rowData.draft && (
            <div className="emailAiPopupTopicSection">
              <h3 className="emailAiPopupSectionTitle">Draft Info</h3>
              <div className="draft-info-container" style={{marginBottom: '15px'}}>
                <div className="draft-info-item">
                  <strong>Agent:</strong> {rowData.draft.agentName} (Case {rowData.draft.agentCaseNumber})
                </div>
                <div className="draft-info-item">
                  <strong>Created:</strong> {new Date(rowData.draft.createdAt || Date.now()).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {/* Topics nur anzeigen wenn es kein Draft ist */}
          {source !== 'draft' && (
            <div className="emailAiPopupTopicSection">
              <div className="topicCircleRow">
                <div className="topicCircleConnectingLine" />
                {(() => {
                  const customReplyTopics = Object.entries(userTopics)
                    .filter(([_, data]) => data.type === 'custom_reply')
                    .map(([id, data]) => ({
                      id,
                      name: data.topic
                    }));
                  
                  const topicsToShow = customReplyTopics.length > 0 
                    ? customReplyTopics 
                    : [
                        { id: 'reply', name: 'Reply To Mail' },
                        { id: 'details', name: 'Send Details' },
                        { id: 'zoom', name: 'Schedule Zoom Call' },
                        { id: 'meetup', name: 'Ask for Meetup' }
                      ];
                  
                  return topicsToShow.map((topic, index) => (
                    <button
                      key={topic.id}
                      className={`topicCircleItem ${selectedTopic === topic.name ? "active" : ""}`}
                      onClick={() => setSelectedTopic(topic.name)}
                    >
                      <span className="topicCircleNumber">{index + 1}</span>
                      <span className="topicCircleLabel">{topic.name}</span>
                    </button>
                  ));
                })()}
              </div>
            </div>
          )}

          <div className="emailAiPopupContextSection">
            <h3 className="emailAiPopupSectionTitle">Email history and info</h3>
            <div className="emailAiPopupContextWrapper">
              <textarea
                className="emailAiPopupContextInput"
                value={contextInfo}
                onChange={(e) => setContextInfo(e.target.value)}
                readOnly
              ></textarea>
            </div>
          </div>
        </div>

        {/* RECHTE SPALTE: Subject, Body, Extra Buttons, Generate/Send */}
        <div className="emailAiPopupRight">
          {/* Absender-E-Mail-Auswahl */}
          <div className="emailAiPopupField">
            <h3 className="emailAiPopupSectionTitle">From</h3>
            <div className="emailAiPopupSenderEmailsContainer">
              {senderEmails.length > 0 ? (
                <div className="emailAiPopupSenderEmailsScroll">
                  {/* Bei Inbox-E-Mails nur die aktuelle Account-E-Mail anzeigen */}
                  <button 
                    key={selectedSenderEmail}
                    className="emailAiPopupSenderEmailButton active"
                  >
                    {(() => {
                      const account = senderEmails.find(item => item.email === selectedSenderEmail);
                      return account && account.sender_name 
                        ? `${account.sender_name} <${account.email}>` 
                        : (account ? account.email : selectedSenderEmail);
                    })()}
                  </button>
                </div>
              ) : (
                <p className="emailAiPopupNoEmails">No sender emails configured.</p>
              )}
            </div>
          </div>

          {/* Empfänger-E-Mail-Anzeige */}
          <div className="emailAiPopupRecipientSection">
            <h3 className="emailAiPopupSectionTitle">To</h3>
            <div className="emailAiPopupTopButtons">
              <div className="emailAiPopupButtonWrapper">
                <button
                  className="emailAiPopupButton active"
                >
                  {selectedEmail}
                </button>
              </div>
            </div>
          </div>

          {/* Betreff und Inhalt */}
          <input
            className="emailAiPopupSubjectInput"
            placeholder="Subject line"
            value={subjectLine}
            onChange={(e) => setSubjectLine(e.target.value)}
            ref={subjectInputRef}
          />

          <div className={`emailAiPopupTexfieldwrapper ${isGenerating || isPerfecting ? 'loading' : ''}`} style={{position: 'relative'}}>
            <div className="salesgod-loading-container">
              <div className="sg-blob-extra"></div>
              <div className="sg-blob-extra"></div>
              <div className="sg-blob-extra"></div>
              <div className="sg-shimmer"></div>
              <img src="/img/salesgod_dark.webp" alt="BonusClub" className="salesgod-loading-logo" />
              <div className="sg-loading-text">
                <span className="sg-loading-subtitle">{isPerfecting ? 'Perfecting your email...' : 'Creating your email...'}</span>
              </div>
            </div>
            <textarea
              id="emailContent"
              className="emailAiPopupTextArea"
              value={bodyContent}
              onChange={(e) => setBodyContent(e.target.value)}
              placeholder={isGenerating || isPerfecting ? "" : "Schreibe deine Antwort hier..."}
            ></textarea>
          </div>
          <EmailAttachmentBar
            userId={userId}
            attachments={attachments}
            onChange={setAttachments}
            disabled={isGenerating || isPerfecting || isSending}
            fileInputRef={attachmentInputRef}
          />
          <div className="emailAiPopupBottomButtons">
            <div className="emailAiPopupActionButtons" ref={actionMenuRef}>
              <button 
                className={`emailAiPopupActionMenuButton ${isActionMenuOpen ? 'active' : ''}`}
                onClick={toggleActionMenu}
                disabled={isGenerating || isPerfecting}
                title="AI Actions"
              >
                <img src="/img/menu_1.svg" alt="AI Actions" className="emailAiPopupActionMenuIcon" />
              </button>
              
                              {isActionMenuOpen && (
                  <div className="emailAiPopupActionMenu">
                    {userSettings.calendlyBookingLink && (
                      <button 
                        className="emailAiPopupActionMenuItem"
                        onClick={handleCopyCalendlyLink}
                      >
                        <div className="emailAiPopupActionMenuItemIconWrapper">
                          <img src="/img/copy.svg" alt="Copy Link" className="emailAiPopupActionMenuItemIcon" />
                        </div>
                        <span>Copy Calendly Link</span>
                      </button>
                    )}
                    {userSettings.calendlyBookingLink && (
                      <button 
                        className="emailAiPopupActionMenuItem"
                        onClick={handleOpenCalendly}
                      >
                        <div className="emailAiPopupActionMenuItemIconWrapper">
                          <img src="/img/calendly.svg" alt="Calendly" className="emailAiPopupActionMenuItemIcon" />
                        </div>
                        <span>Open Calendly</span>
                      </button>
                    )}
                    <button 
                      className="emailAiPopupActionMenuItem"
                      onClick={handlePerfectAndClose}
                      disabled={!bodyContent || bodyContent.trim() === ""}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <img src="/img/magic-wand.svg" alt="Perfect" className="emailAiPopupActionMenuItemIcon" />
                      </div>
                      <span>Make Perfect <span className="aiLabel">AI</span></span>
                    </button>
                    <button 
                      className="emailAiPopupActionMenuItem"
                      onClick={handleGenerateAndClose}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <img src="/img/refresh-arrow.svg" alt="Generate" className="emailAiPopupActionMenuItemIcon" />
                      </div>
                      <span>Re-Generate <span className="aiLabel">AI</span></span>
                    </button>
                    <button
                      className="emailAiPopupActionMenuItem"
                      onClick={() => {
                        setIsActionMenuOpen(false);
                        if (attachmentInputRef.current) attachmentInputRef.current.click();
                      }}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                        </svg>
                      </div>
                      <span>Attach File</span>
                    </button>
                  </div>
                )}
            </div>
            <button
              className={`emailAiPopupSendButton ${sendSuccess ? 'success' : ''} ${isSending ? 'sending' : ''} ${!isEmailValid() ? 'disabled' : ''}`}
              onClick={handleSend}
              disabled={isGenerating || isSending || !isEmailValid() || isPerfecting}
            >
              {isSending ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Sending...</span>
                </>
              ) : sendSuccess ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Sent</span>
                </>
              ) : (
                <>
                  <span>Send Reply</span>
                  <img src="/img/sent.svg" alt="Send" className="emailAiPopupSendIcon" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Perfect Slider Popup */}
      {perfectSliderOpen && (
        <div className="perfect-slider-overlay" onClick={() => setPerfectSliderOpen(false)}>
          <div className="perfect-slider-popup" onClick={e => e.stopPropagation()}>
            <div className="perfect-slider-header">
              <svg className="perfect-slider-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="url(#sparkleGradI)" stroke="url(#sparkleGradI)" strokeWidth="0.5" strokeLinejoin="round"/>
                <defs><linearGradient id="sparkleGradI" x1="3" y1="2" x2="21" y2="22" gradientUnits="userSpaceOnUse"><stop stopColor="#6897B7"/><stop offset="1" stopColor="#2a6776"/></linearGradient></defs>
              </svg>
              <h3>Make Perfect</h3>
            </div>
            <p className="perfect-slider-desc">
              {perfectCreativity <= 2 ? 'Fix typos & grammar only — keep text almost identical'
                : perfectCreativity <= 4 ? 'Polish wording & improve clarity — same structure'
                : perfectCreativity <= 6 ? 'Rephrase & enhance — noticeable improvements'
                : perfectCreativity <= 8 ? 'Substantial rewrite — fresh phrasing, same intent'
                : 'Completely new email — new angle, new structure'}
            </p>
            <div className="perfect-slider-track">
              <input
                type="range"
                min="1"
                max="10"
                value={perfectCreativity}
                onChange={e => setPerfectCreativity(Number(e.target.value))}
                className="perfect-slider-input"
              />
              <div className="perfect-slider-steps">
                {[
                  { val: 1, label: 'Fix' },
                  { val: 3, label: 'Polish' },
                  { val: 5, label: 'Enhance' },
                  { val: 7, label: 'Rewrite' },
                  { val: 10, label: 'New' },
                ].map(s => (
                  <span
                    key={s.val}
                    className={`perfect-slider-step ${perfectCreativity >= s.val ? 'active' : ''}`}
                    onClick={() => setPerfectCreativity(s.val)}
                  >{s.label}</span>
                ))}
              </div>
            </div>
            <div className="perfect-slider-actions">
              <button className="perfect-slider-cancel" onClick={() => setPerfectSliderOpen(false)}>Cancel</button>
              <button className="perfect-slider-go" onClick={() => { setPerfectSliderOpen(false); handlePerfectEmail(perfectCreativity); }}>
                GO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxModeEmail; 